/**
 * The totals row of a marking — the one line that closes the criteria table.
 *
 * **Why this module exists.** A Technical Delegate looked at the closing line of his marking
 * table and could not tell what it meant. It read:
 *
 * ```
 * Performance Outcome | 200 of 200 | 76
 * ```
 *
 * Three numbers, none of them labelled. `200 of 200` is the weight still counting towards
 * the mark, out of the full weight of the set; `76` is the mark itself, out of 100. Read
 * cold, `200` looks like a score and `76` looks like a score out of 200. The row was
 * arithmetically correct and completely unreadable.
 *
 * So the row now says what each number is:
 *
 * ```
 * Performance Outcome | Weight in play 200 / 200 | 76 / 100
 * ```
 *
 * and, when some criterion has been left neither marked nor set to N/A, it says that too,
 * beside the mark it quietly weakens:
 *
 * ```
 * Performance Outcome | Weight in play 170 / 200 | 76 / 100 · 2 unmarked
 * ```
 *
 * **Why it lives in `packages/shared` and not beside the screen.** The same row is drawn
 * three times over — on the marking panel, in the Word document the TD downloads, and in the
 * text `Copy For TMS` puts on the clipboard. Two of those three are produced here, in
 * `tms-push.ts`, and cannot reach into the web app. One formatter in the one place all three
 * can see it is the only arrangement in which they cannot drift apart, and drifting apart is
 * exactly how a row ends up meaning one thing on screen and another on paper.
 *
 * **The separator is a middle dot, not a badge or a colour.** The mark cell has to survive
 * being pasted into a TMS form field as plain text, so anything that carries meaning only
 * when it is styled would be lost precisely where the TD is least able to check it.
 *
 * Nothing here computes a mark. The outcome arrives already computed by
 * `computePerformanceOutcome`, the single implementation of the weighted formula; this module
 * only decides how to say it.
 */
/** The label the row carries, in every rendering. */
export const TOTALS_ROW_LABEL = "Performance Outcome";
/** The words in front of the weight figures, in every rendering. */
export const TOTALS_ROW_WEIGHT_LABEL = "Weight in play";
/** What the row says when no weighted criterion has been marked yet. */
export const TOTALS_ROW_NOT_MARKED = "Not marked yet";
/**
 * The totals row for one marking.
 *
 * A criterion set to **N/A** is a deliberate statement that it does not apply here, so its
 * weight leaves the figure on the left: `Weight in play 170 / 200` says thirty points of
 * weight have been set aside. A criterion simply **not marked yet** is a different thing —
 * it is an omission, not a decision — so it keeps its weight and is counted on the right
 * instead, where it sits next to the mark it is holding back.
 */
export function formatMarksTotalsRow(input) {
    const weightValue = `${String(input.liveWeightTotal)} / ${String(input.weightTotal)}`;
    const markValue = input.outcome === null ? TOTALS_ROW_NOT_MARKED : `${String(input.outcome)} / 100`;
    const unmarkedNote = input.unmarkedCount > 0
        ? `${String(input.unmarkedCount)} unmarked`
        : null;
    return {
        label: TOTALS_ROW_LABEL,
        weightLabel: TOTALS_ROW_WEIGHT_LABEL,
        weightValue,
        weightText: `${TOTALS_ROW_WEIGHT_LABEL} ${weightValue}`,
        markValue,
        markText: unmarkedNote === null ? markValue : `${markValue} · ${unmarkedNote}`,
        unmarkedNote,
    };
}
/**
 * The same row, taken straight off anything shaped like a marks payload.
 *
 * Deliberately structural rather than typed to `TmsMarksPayload`: it keeps this module free
 * of any import from `tms-push.ts`, which imports *this* one, and it lets the marking screen
 * hand over the payload it has already built for the push dialogue. One line per criterion is
 * assumed — which is what `buildMarksPayload` produces, unmarked criteria included — because
 * the unmarked count is read off that list.
 */
export function marksTotalsRow(source) {
    return formatMarksTotalsRow({
        outcome: source.outcome,
        weightTotal: source.weightTotal,
        liveWeightTotal: source.liveWeightTotal,
        unmarkedCount: source.marks.filter((mark) => !mark.na && mark.value === null).length,
    });
}
//# sourceMappingURL=marks-totals-row.js.map